import { useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';

import Page from 'components/layouts/page/Page';
import Button from 'components/shared/Button';
import FilterBar from 'components/shared/filters/FilterBar';
import { FilterSection } from 'components/shared/filters/FilterSlideout';
import Tabs, { TabType } from 'components/Tabs';
import { FILTER_TYPES } from 'constants/filterConfigs';
import { usePortalConnections, usePortalName } from 'hooks/usePortalConnections';
import FilterValueArray from 'models/filterValues/FilterValueArray';
import { FilterState } from 'stores/types';
import { Body, BodySmall } from 'styles/typography';

import { LIST_STATUS_DISPLAY } from '../referralProgress';

import ManageConnectedPortalsModal from './connectedPortals/ManageConnectedPortalsModal';
import { TAB_LABELS, tabFromSegment, tabPath, TASKS_ROUTES } from './constants';
import { ManualLaunchProvider } from './ManualLaunchProvider';
import PortalScansTab from './PortalScansTab';
import RunningTasksStrip from './RunningTasksStrip';
import { TASK_REASON_LABELS, TaskReason } from './taskCardCopy';
import {
  hasServerFilters,
  NO_TASK_DATE_RANGE,
  operationalTaskCards,
  patientTaskCard,
  TASK_STATUSES,
  TaskDateRange,
  TaskFilters,
  TaskServerFilters,
  TaskSort,
  TaskStatus,
} from './taskCards';
import TaskSlideout from './TaskSlideout';
import TasksTab from './TasksTab';
import TasksToolbar from './TasksToolbar';
import useRunningHeadless from './useRunningHeadless';
import useTasks from './useTasks';

// The app-standard filter shape (FilterBar -> FilterSlideout -> FilterMultiSelect): a record of
// FilterValueArrays holding {id, name} options. The tab filters on plain string arrays, so the
// applied values are projected down before they reach the cards.
export type TasksFilterState = FilterState<FilterValueArray>;

const NO_FILTERS: TasksFilterState = {
  sourceSystems: new FilterValueArray([]),
  reasons: new FilterValueArray([]),
  statuses: new FilterValueArray([]),
};

// Offered statically rather than derived from the result set: the list is already
// server-filtered on this axis, so presence can't be read off what came back.
const STATUS_OPTIONS = TASK_STATUSES.map((status) => ({ id: status, name: LIST_STATUS_DISPLAY[status].label }));

/**
 * `Inbound Referrals -> Tasks`. ONE route with a tab parameter rather than two routes: the
 * header, the active-scan banner and the `Manage Connected Portals` link are shared by both
 * tabs, and a second route would fork all three.
 */
export default function TasksPage() {
  const { tab: segment } = useParams();
  const navigate = useNavigate();
  const tab = tabFromSegment(segment);
  const { connections, isPending: connectionsPending } = usePortalConnections();
  const portalName = usePortalName();
  // `?task=<workflowId>` deep-links the task slideout (OLIO-3107 — the extension HUD's
  // needs_action destination). Seeded once from the URL; the slideout loads by id, so
  // the workflow doesn't need to be in the (action_required-filtered) list. No siblings:
  // a deep link arrives without a list to traverse.
  const [searchParams, setSearchParams] = useSearchParams();
  const [openTask, setOpenTask] = useState<{ id: string; siblings: string[] } | null>(() => {
    const task = searchParams.get('task');
    return task ? { id: task, siblings: [] } : null;
  });

  // Keep the param synced with the open slideout so the URL stays shareable, without
  // stacking a history entry per open/close.
  useEffect(() => {
    setSearchParams(
      (params) => {
        if (openTask) params.set('task', openTask.id);
        else params.delete('task');
        return params;
      },
      { replace: true }
    );
  }, [openTask, setSearchParams]);
  const [dismissedScans, setDismissedScans] = useState<string[]>([]);
  const [managing, setManaging] = useState<{ sourceSystem: string | null } | null>(null);
  // Sort and filters live here rather than in the tab: their controls (the FilterBar and the
  // toolbar band) render outside the tab's own tree. Sort is declared before the list fetch
  // because the fetch takes it — the pages come back already ordered.
  const [sort, setSort] = useState<TaskSort>('newest');
  const [filters, setFilters] = useState<TasksFilterState>(NO_FILTERS);
  // The created range lives outside the slideout (the toolbar ribbon), so it is its own state.
  const [createdAt, setCreatedAt] = useState<TaskDateRange>(NO_TASK_DATE_RANGE);
  // The start-scan gesture lives in the page header; the modal it opens (and the mutation
  // behind it) stay the scans tab's own.
  const [startScanOpen, setStartScanOpen] = useState(false);
  // What the cards actually filter on — the option ids, projected out of the FilterValueArrays.
  const appliedFilters: TaskFilters = useMemo(
    () => ({
      sourceSystems: (filters.sourceSystems ?? []).map((option) => String(option.id)),
      reasons: (filters.reasons ?? []).map((option) => String(option.id) as TaskReason),
    }),
    [filters]
  );
  const serverFilters: TaskServerFilters = useMemo(
    () => ({ statuses: (filters.statuses ?? []).map((option) => String(option.id) as TaskStatus), createdAt }),
    [filters, createdAt]
  );
  const { workflows, totalRecords, query, isLoading, isError, isStale } = useTasks(sort, serverFilters);
  // Headless runs in flight — the piece of the picture the attention query above deliberately
  // omits. Rides the same nudge (shared list-key prefix), so rows appear when a run dispatches
  // and remove themselves when it settles.
  const { workflows: runningHeadless } = useRunningHeadless();

  const patientCards = useMemo(
    () => workflows.map((workflow) => patientTaskCard(workflow, { portalName })),
    [workflows, portalName]
  );
  const operational = useMemo(() => operationalTaskCards(connections), [connections]);

  // Only the axes actually present in the current result set are offered — a filter for a
  // portal this client has never seen is a dead control.
  const sourceSystemOptions = useMemo(() => {
    const systems = [...new Set(patientCards.map((card) => card.sourceSystem).filter(Boolean))];
    return systems.sort().map((system) => ({ id: system, name: portalName(system) }));
  }, [patientCards, portalName]);

  const reasonOptions = useMemo(
    () =>
      ([...new Set(patientCards.map((card) => card.reason))].sort() as TaskReason[]).map((reason) => ({
        id: reason,
        name: TASK_REASON_LABELS[reason],
      })),
    [patientCards]
  );

  const filterSections: FilterSection[] = useMemo(
    () => [
      {
        title: 'Source System',
        filters: [
          {
            filterType: FILTER_TYPES.DROPDOWN,
            name: 'sourceSystems',
            placeholder: 'Source system',
            options: sourceSystemOptions,
            getOptionLabel: (option) => option.name ?? '',
            getOptionValue: (option) => option.id ?? '',
          },
        ],
      },
      {
        title: 'Reason',
        filters: [
          {
            filterType: FILTER_TYPES.DROPDOWN,
            name: 'reasons',
            placeholder: 'Reason',
            options: reasonOptions,
            getOptionLabel: (option) => option.name ?? '',
            getOptionValue: (option) => option.id ?? '',
          },
        ],
      },
      {
        title: 'Status',
        filters: [
          {
            filterType: FILTER_TYPES.DROPDOWN,
            name: 'statuses',
            placeholder: 'Status',
            options: STATUS_OPTIONS,
            getOptionLabel: (option) => option.name ?? '',
            getOptionValue: (option) => option.id ?? '',
          },
        ],
      },
    ],
    [sourceSystemOptions, reasonOptions]
  );

  // The scans tab filters on one axis only: which portal. Its options come from the
  // connections rather than the loaded scan rows — the rows page, so "portals seen so far"
  // would grow as the person scrolls.
  const scanFilterSections: FilterSection[] = useMemo(
    () => [
      {
        title: 'Source System',
        filters: [
          {
            filterType: FILTER_TYPES.DROPDOWN,
            name: 'sourceSystems',
            placeholder: 'Source system',
            options: connections
              .filter((connection) => !connection.isManual)
              .map((connection) => ({ id: connection.sourceSystem, name: connection.displayName })),
            getOptionLabel: (option) => option.name ?? '',
            getOptionValue: (option) => option.id ?? '',
          },
        ],
      },
    ],
    [connections]
  );

  const clearFilters = () => {
    setFilters(NO_FILTERS);
    setCreatedAt(NO_TASK_DATE_RANGE);
  };

  // The tab bar must survive having one tab: `Portal Scans` doesn't exist in M0/M1, where
  // nothing could ever start a scan. The gate is the MODE, not readiness — a portal in
  // extension or headless mode shows the tab before any scan is recorded, because the tab
  // (its empty state, the start modal's per-row reasons, Manage Connected Portals) is where
  // a person discovers what's still missing. Hiding it on `canScan` would bury that path.
  const scanCapable = connections.some((connection) => !connection.isManual);
  // The count includes operational cards: the design shows `Tasks (1)` on a frame whose only
  // card is the setup one. `totalRecords` rather than the loaded rows — the list pages, and
  // the tab label counts what exists, not what has scrolled into view.
  const taskCount = totalRecords + operational.length;

  const tabs: TabType[] = [
    // `end`: this tab's path is a prefix of the Portal Scans URL, and without an exact match
    // both tabs read as selected on that tab.
    { label: `${TAB_LABELS.tasks} (${taskCount})`, value: 'tasks', to: tabPath('tasks'), end: true },
    ...(scanCapable ? [{ label: TAB_LABELS.portalScans, value: 'portalScans', to: tabPath('portalScans') }] : []),
  ];

  // Live scans, from the connection rows' own `liveScanWorkflowId`. Dismissal is keyed on that
  // id rather than on a boolean, so dismissing today's banner doesn't suppress tomorrow's scan.
  const scanning = connections.filter(
    (connection) => connection.liveScanWorkflowId && !dismissedScans.includes(connection.liveScanWorkflowId)
  );

  // Opened with a portal when the gesture came from a portal-level card (it already names the
  // one that needs attention), and on the list when it came from the header.
  const managePortals = (sourceSystem?: string) => setManaging({ sourceSystem: sourceSystem ?? null });

  if (!tab) return <Navigate to={TASKS_ROUTES.BASE} replace />;
  // A deep link to a tab this client can't have (mode changed, or a shared URL) lands on Tasks
  // rather than on a blank frame — but only once the connections have actually loaded. On
  // first render the query is still pending and `scanCapable` is false for everyone, so
  // redirecting then would bounce every legitimate deep link and refresh back to Tasks.
  if (tab === 'portalScans' && !scanCapable && !connectionsPending) return <Navigate to={TASKS_ROUTES.BASE} replace />;

  const title = tab === 'portalScans' ? TAB_LABELS.portalScans : TAB_LABELS.tasks;

  // `Start a Portal Scan` is the header's right-hand action on the scans tab. It occupies its
  // slot on BOTH tabs — hidden on Tasks — so the title row is the same height everywhere and
  // switching tabs doesn't shift the header, the hairline, or everything below them.
  const startScanButton = (
    <HiddenSlot $hidden={tab !== 'portalScans'}>
      <Button variant='outline-primary' size='small' onClick={() => setStartScanOpen(true)} data-testid='start-scan'>
        Start a Portal Scan
      </Button>
    </HiddenSlot>
  );

  return (
    <ManualLaunchProvider>
      <Page>
        {/* The app-standard header: FilterBar owns the title row, the `Filters (N)` button and
            the filter slideout (desktop and mobile both). The tab strip rides inside it so the
            tabs sit flush against the header's bottom border rather than floating below it. */}
        <FilterBar<TasksFilterState>
          title={title}
          filters={filters}
          filterSections={tab === 'tasks' ? filterSections : scanFilterSections}
          applyFilters={(next) =>
            // Merged rather than replaced: the scans tab's slideout carries no Reason or
            // Status section, and applying there must not silently drop those filters.
            setFilters(
              (previous) =>
                ({
                  sourceSystems: next.sourceSystems ?? previous.sourceSystems,
                  reasons: next.reasons ?? previous.reasons,
                  statuses: next.statuses ?? previous.statuses,
                }) as TasksFilterState
            )
          }
          onClearFilters={clearFilters}
          showSearchInput={false}
          showResults={false}
          callToAction={startScanButton}
          callToActionShowMinWidth={800}>
          <TabsRow>
            <Tabs tabs={tabs} onTabClick={(clicked) => navigate(tabPath(clicked.value as 'tasks' | 'portalScans'))} />
          </TabsRow>
        </FilterBar>
        <Page.Content>
          {/* Inside Page.Content — the scroll container — on purpose: the sort/manage ribbon
              and the banners scroll away with the cards rather than pinning below the header.
              `Manage Connected Portals` is shared by both tabs — M0/M1 have no connected
              portal to manage, so it's mode-gated on the same verdict the Portal Scans tab
              uses. Sort is shared: both lists order by created-at, and the toolbar row is one. */}
          <TasksToolbar
            sort={sort}
            onSortChange={setSort}
            createdAt={tab === 'tasks' ? createdAt : undefined}
            onCreatedAtChange={setCreatedAt}
            onManagePortals={scanCapable ? () => managePortals() : undefined}
          />
          {scanning.map((connection) => (
            <Banner key={connection.liveScanWorkflowId} data-testid='scan-banner'>
              <BodySmall>
                {connection.displayName} scan actively in progress. New tasks as a result of the scan will be added
                below.
              </BodySmall>
              <DismissButton
                type='button'
                onClick={() => setDismissedScans((ids) => [...ids, connection.liveScanWorkflowId!])}>
                Dismiss
              </DismissButton>
            </Banner>
          ))}
          {/* A dead socket looks exactly like a page with no work on it, which is the one
              wrong impression this surface can leave. Say so rather than quietly polling. */}
          {isStale && (
            <Banner data-testid='tasks-stale-banner'>
              <BodySmall>
                Live updates aren’t coming through right now. This list still refreshes every few minutes, and reloading
                the page will bring it up to date.
              </BodySmall>
            </Banner>
          )}
          {tab === 'tasks' && <RunningTasksStrip workflows={runningHeadless} portalName={portalName} />}
          {tab === 'tasks' ? (
            <TasksTab
              patientCards={patientCards}
              operational={operational}
              isLoading={isLoading}
              isError={isError}
              connections={connections}
              sort={sort}
              filters={appliedFilters}
              serverFiltered={hasServerFilters(serverFilters)}
              onClearFilters={clearFilters}
              query={query}
              onOpenTask={(workflowId, siblings) => setOpenTask({ id: workflowId, siblings })}
              onManagePortals={managePortals}
            />
          ) : (
            <PortalScansTab
              connections={connections}
              startScanOpen={startScanOpen}
              onStartScanClose={() => setStartScanOpen(false)}
              // A results row opens the task its referral became, in the same one slideout the
              // Tasks tab uses. The traversal order is that scan's own created rows.
              onOpenTask={(workflowId, siblings) => setOpenTask({ id: workflowId, siblings })}
              sort={sort}
              sourceSystems={appliedFilters.sourceSystems}
              onClearFilters={clearFilters}
            />
          )}
        </Page.Content>
        {managing && (
          <ManageConnectedPortalsModal initialSourceSystem={managing.sourceSystem} onClose={() => setManaging(null)} />
        )}
        <TaskSlideout
          workflowId={openTask?.id ?? null}
          siblingWorkflowIds={openTask?.siblings ?? []}
          onNavigate={(workflowId) => setOpenTask((open) => (open ? { ...open, id: workflowId } : open))}
          onClose={() => setOpenTask(null)}
        />
      </Page>
    </ManualLaunchProvider>
  );
}

// Bleeds across FilterBar's 24px padding so the strip runs edge to edge and the active tab's
// underline lands on the header's own bottom border, as drawn. The hairline above separates
// the title row from the tabs, same as the patient story page's tab band.
// `visibility` rather than conditional render: the slot keeps its width and height while
// hidden, which is what pins the header's size across tabs.
const HiddenSlot = styled.div<{ $hidden: boolean }>`
  visibility: ${({ $hidden }) => ($hidden ? 'hidden' : 'visible')};
`;

const TabsRow = styled.div`
  /* The negative side margins subtract from the rendered width, so the basis has to hand that
     48px back or the hairline stops short of the right padding edge. */
  flex: 0 0 calc(100% + 48px);
  margin: 0 -24px -24px;
  padding: 0 24px;
  border-top: 1px solid ${({ theme }) => theme.colors.black10};
`;

const Banner = styled(Body).attrs({ as: 'div' })`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 12px;
  padding: 12px 16px;
  border-radius: 8px;
  background-color: ${({ theme }) => theme.colors.blue10};
  color: ${({ theme }) => theme.colors.black75};
`;

const DismissButton = styled.button`
  font-family: ${({ theme }) => theme.textStyles.base.fontFamily};
  font-size: 12px;
  color: ${({ theme }) => theme.colors.blue100};
  background: none;
  border: none;
  cursor: pointer;
`;
